import { useEffect, useId, useRef, useState } from 'react';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import { consolidatedBusinessApi } from '../../services/consolidatedBusinessApi';
import { useTableSort } from '../../hooks/useTableSort';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { SortableHeader, type SortState } from '../ui/SortableHeader';
import { useDialog } from '../ui/useDialog';
import { fmtDate } from './format';
import type { ConsBusinessTransaction, ConsBusinessTransactionsResponse } from '../../types/consolidatedBusiness';

interface Props {
    /** base64url(JSON([CustomerID, ProductName])) — built by buildGroupKey or served by the API. */
    groupKey: string;
    productName: string | null;
    customerName: string | null;
    customerId: string | null;
    onClose: () => void;
}

/** Money with thousands separators. Null → dash. */
function fmtMoney(v: number | null, digits = 0): string {
    if (v == null) return '–';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(v);
}

/**
 * Unit prices at FOUR decimals. Two is not enough: these run around 1.43 EUR/kg, so rounding to
 * cents throws away exactly the drift the modal exists to show.
 */
function fmtPrice(v: number | null): string {
    if (v == null) return '–';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 4, maximumFractionDigits: 4,
    }).format(v);
}

function fmtQty(v: number | null): string {
    if (v == null) return '–';
    return new Intl.NumberFormat('de-CH', { maximumFractionDigits: 1 }).format(v);
}

function fmtPct(v: number | null): string {
    return v == null ? '–' : `${v.toFixed(1)}%`;
}

type TxSortKey = 'postingDate' | 'invoiceNo' | 'documentType' | 'sourceType' | 'quantity'
    | 'salesPriceEUR' | 'materialPriceEUR' | 'netSalesEUR' | 'grossProfitEUR' | 'gpPercent';

/**
 * The house table (design audit 2026-10-07, S2-03): sticky, sortable by keyboard with
 * `aria-sort`, and resizable — it was the one table on the page without resizing. Money columns
 * carry their unit (S1-06): every figure here is EUR, whatever the KPI toggle shows outside.
 */
const TX_COLUMNS: { key: TxSortKey; label: string; width: number; align: 'left' | 'right' }[] = [
    { key: 'postingDate', label: 'Posting Date', width: 105, align: 'left' },
    { key: 'invoiceNo', label: 'Invoice', width: 120, align: 'left' },
    { key: 'documentType', label: 'Type', width: 140, align: 'left' },
    { key: 'sourceType', label: 'Source', width: 90, align: 'left' },
    { key: 'quantity', label: 'Quantity', width: 110, align: 'right' },
    { key: 'salesPriceEUR', label: 'Sales Price (EUR)', width: 125, align: 'right' },
    { key: 'materialPriceEUR', label: 'Material Price (EUR)', width: 140, align: 'right' },
    { key: 'netSalesEUR', label: 'Net Sales (EUR)', width: 120, align: 'right' },
    { key: 'grossProfitEUR', label: 'Gross Profit (EUR)', width: 130, align: 'right' },
    { key: 'gpPercent', label: 'GP%', width: 70, align: 'right' },
];

const CREDIT_MEMO_HINT =
    'Credit memo: quantity and amounts are negative, so the derived unit prices are already ' +
    'correct and positive, while GP% is negated relative to an invoice.';

/**
 * Every invoice behind one consolidated-business combination — opened by right-clicking a row.
 *
 * Rows are grouped per invoice server-side across ALL batch variants of the group, so one invoice
 * number appears exactly once even when the ERP split it across batches or between stock and
 * direct; `lineCount` says how many source rows were folded in. Credit memos keep their negative
 * quantity and amount, which makes the derived unit prices correct without any absolute value.
 *
 * Sales price and material price are derived, not stored: net sales / quantity and
 * (net sales − gross profit) / quantity.
 */
export function ConsBusinessTransactionsModal({
    groupKey, productName, customerName, customerId, onClose,
}: Props) {
    const [data, setData] = useState<ConsBusinessTransactionsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // A real dialog (design audit S1-03): focus in, Tab kept inside, Esc closes, focus back.
    const panel = useRef<HTMLDivElement>(null);
    const titleId = useId();
    useDialog(panel, onClose);
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(TX_COLUMNS, 'consbusiness-tx-col-widths');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        consolidatedBusinessApi.getTransactions(groupKey)
            .then(r => { if (!cancelled) setData(r); })
            .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load transactions'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [groupKey]);

    const rows: ConsBusinessTransaction[] = data?.transactions ?? [];
    const { sorted, sort, toggleSort } = useTableSort(rows, {
        postingDate: r => r.postingDate,
        invoiceNo: r => r.invoiceNo,
        documentType: r => r.documentType,
        sourceType: r => r.sourceType,
        quantity: r => r.quantity,
        salesPriceEUR: r => r.salesPriceEUR,
        materialPriceEUR: r => r.materialPriceEUR,
        netSalesEUR: r => r.netSalesEUR,
        grossProfitEUR: r => r.grossProfitEUR,
        gpPercent: r => r.gpPercent,
    }, { key: 'postingDate', dir: 'desc' });

    const totals = data?.totals;
    // Only qualifies the header when the whole group shares one unit.
    const unit = data?.salesUnit ?? null;
    // Comes from the server, never derived from `unit === null`: a group that records no unit at
    // all also has a null unit, and it is not mixing anything.
    const mixedUnits = data?.mixedUnits === true;

    const headerSort: SortState<TxSortKey> = { key: sort.key as TxSortKey | null, direction: sort.dir };
    const onSort = (key: TxSortKey) => {
        // A drag ends with a mouseup in the header, which the browser reports as a click.
        if (shouldSuppressClick()) return;
        toggleSort(key);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={onClose}>
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="flex max-h-[85vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
                    <div>
                        <h2 id={titleId} className="text-sm font-medium text-gray-800">Transaction History</h2>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {productName ?? '–'}
                            <span className="mx-1.5 text-gray-300">·</span>
                            <span className="text-gray-500">{customerName ?? '–'}</span>
                            {customerId && (
                                <>
                                    <span className="mx-1.5 text-gray-300">·</span>
                                    <span className="text-gray-500 tabular-nums">{customerId}</span>
                                </>
                            )}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                        <X aria-hidden="true" className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="animate-pulse space-y-2 p-8">
                            <div className="h-4 w-1/3 rounded bg-gray-100" />
                            <div className="h-32 rounded bg-gray-100" />
                        </div>
                    ) : error ? (
                        <div role="alert" className="flex items-center justify-center gap-2 p-8 text-sm text-red-600">
                            <AlertCircle className="h-4 w-4" /> {error}
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="p-8 text-center text-sm text-gray-500">
                            No invoiced transactions for this combination.
                        </p>
                    ) : (
                        <table className="w-full table-fixed text-xs" style={{ minWidth: totalWidthOf(TX_COLUMNS.map(c => c.key)) }}>
                            <colgroup>
                                {TX_COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                            </colgroup>
                            <thead>
                                <tr>
                                    {TX_COLUMNS.map(c => (
                                        <SortableHeader
                                            key={c.key}
                                            label={c.key === 'quantity' && unit ? `Quantity (${unit})` : c.label}
                                            sortKey={c.key}
                                            sort={headerSort}
                                            onSort={onSort}
                                            align={c.align}
                                            onResizeStart={x => startResize(c.key, x)}
                                            onResetWidth={() => resetWidth(c.key)}
                                            isResizing={resizingKey === c.key}
                                        />
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(r => {
                                    const isCredit = r.documentType === 'Sales Credit Memo';
                                    return (
                                        <tr key={r.invoiceNo} className="hover:bg-gray-50">
                                            <td className="px-3 py-1.5 text-gray-500">{fmtDate(r.postingDate)}</td>
                                            <td className="px-3 py-1.5 text-gray-900">
                                                {r.invoiceNo}
                                                {r.lineCount > 1 && (
                                                    <span
                                                        className="ml-1.5 text-[11px] text-gray-500"
                                                        title={`${r.lineCount} lines on this invoice`}
                                                    >
                                                        {r.lineCount}×
                                                    </span>
                                                )}
                                            </td>
                                            <td
                                                // Neutral, not amber: amber means somebody has to act,
                                                // and a credit memo is a fact (design audit S2-09).
                                                className={`px-3 py-1.5 ${isCredit ? 'font-medium text-gray-700' : 'text-gray-600'}`}
                                                title={isCredit ? CREDIT_MEMO_HINT : undefined}
                                            >
                                                {r.documentType ?? '–'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600">{r.sourceType ?? '–'}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums">
                                                {fmtQty(r.quantity)}
                                                {mixedUnits && r.salesUnit && (
                                                    <span className="ml-1 text-[11px] text-gray-500">{r.salesUnit}</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-medium tabular-nums">{fmtPrice(r.salesPriceEUR)}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{fmtPrice(r.materialPriceEUR)}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtMoney(r.netSalesEUR)}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums">{fmtMoney(r.grossProfitEUR)}</td>
                                            <td className={`px-3 py-1.5 text-right tabular-nums ${
                                                r.gpPercent != null && r.gpPercent < 0 ? 'text-red-600' : 'text-gray-600'
                                            }`}>{fmtPct(r.gpPercent)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {totals && (
                                <tfoot className="sticky bottom-0 border-t border-gray-200 bg-gray-50 font-medium">
                                    <tr>
                                        <td className="px-3 py-2 text-gray-600" colSpan={4}>
                                            {totals.invoiceCount} invoice{totals.invoiceCount === 1 ? '' : 's'}
                                            <span className="ml-1 font-normal text-gray-500">(weighted average)</span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {mixedUnits ? '–' : fmtQty(totals.quantity)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {mixedUnits ? '–' : fmtPrice(totals.avgSalesPriceEUR)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                                            {mixedUnits ? '–' : fmtPrice(totals.avgMaterialPriceEUR)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.netSalesEUR)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(totals.grossProfitEUR)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtPct(totals.gpPercent)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    )}

                    {totals?.truncated && (
                        <p className="flex items-center gap-1.5 border-t border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            Showing the first 1000 invoices — older transactions are not listed, and the totals
                            above cover only what is shown.
                        </p>
                    )}
                    {mixedUnits && (
                        <p className="flex items-center gap-1.5 border-t border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                            This combination mixes sales units — quantities and unit prices are not comparable
                            and are not totalled.
                        </p>
                    )}
                </div>

                {!loading && !error && rows.length > 0 && (
                    <p className="border-t border-gray-100 px-5 py-2 text-[11px] text-gray-500">
                        Invoiced sales only, grouped per invoice.
                        Commission business is excluded here and in the row's KPIs.
                    </p>
                )}
            </div>
        </div>
    );
}
